/**
 * The middle pane while Drafts or Scheduled is open in the rail, and the empty
 * reading pane beside it.
 *
 * The same row shape as the inbox, so the two lists read as one product: who it
 * goes to, when, the subject and the first words. Drafts are ordered by when
 * they were last touched and show that time; scheduled letters are a queue,
 * soonest first, and show when they will go - with a clock, because "09:00"
 * on its own reads as when something arrived.
 *
 * A draft the scheduler could not send says why on its own line, in red. It is
 * the only place that failure is ever shown, so it cannot be a tooltip.
 */
import { forwardRef, type ReactNode } from "react";
import { AlertTriangle, CalendarClock, FileText, Loader2, Paperclip } from "lucide-react";
import { cn } from "@wa/ui";
import { useI18n, useT } from "../../../../i18n/I18nProvider";
import type { DraftListItem } from "../../../../lib/mailDrafts";
import { Avatar, fmtStamp } from "../../../ui";
import { ContextMenu, useContextMenu } from "../../../shell/ContextMenu";
import { RailTrigger } from "../../_shared/workMobile";
import { ReadingPaneToggle } from "../../_shared/ReadingPaneToggle";
import type { ReadingPane } from "../../_shared/readingPane";
import { formatSendTime } from "./draftTime";
import { draftErrorText } from "./draftErrors";

export type DraftKind = "drafts" | "scheduled";

export const DraftList = forwardRef<
  HTMLDivElement,
  {
    kind: DraftKind;
    rows: DraftListItem[];
    loading: boolean;
    onOpen: (row: DraftListItem) => void;
    renderMenu: (row: DraftListItem, close: () => void) => ReactNode;
    readingPane: ReadingPane;
    onReadingPane: (next: ReadingPane) => void;
    /** Opens the folder rail. Only reachable on a phone, where it is a sheet. */
    onRail: () => void;
  }
>(function DraftList({ kind, rows, loading, onOpen, renderMenu, readingPane, onReadingPane, onRail }, ref) {
  const t = useT();
  const { locale } = useI18n();
  const menu = useContextMenu<DraftListItem>();

  return (
    <div ref={ref} className="crm-card crm-work-col crm-work-list">
      <div className="crm-pane-head">
        <RailTrigger label={t("mail.folders")} onOpen={onRail} />
        <span className="min-w-0 flex-1">
          <span className="crm-trunc block text-[13.5px] font-[650]">{t(`drafts.${kind}Title`)}</span>
          <span className="crm-mut mt-0.5 block text-[11.25px]">{t(`drafts.${kind}Hint`)}</span>
        </span>
        {loading ? <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" /> : null}
        <ReadingPaneToggle pane={readingPane} onPane={onReadingPane} />
      </div>

      <div className="crm-pane-scroll">
        {rows.length === 0 ? (
          <div className="crm-pane-empty">{loading ? t("drafts.loading") : t(`drafts.${kind}Empty`)}</div>
        ) : (
          rows.map((row) => {
            const who = row.to.join(", ") || (row.mode === "reply" ? t("drafts.replyTo") : t("drafts.noRecipient"));
            return (
              <button
                key={row.id}
                type="button"
                className="crm-item"
                onClick={() => onOpen(row)}
                {...menu.rowProps(row)}
              >
                <Avatar name={row.to[0] || "?"} />
                <span className="min-w-0 flex-1">
                  <span className="crm-item-top">
                    <span className="crm-item-from">{who}</span>
                    <span className={cn("crm-item-time", row.scheduledAt && "flex items-center gap-1")}>
                      {row.scheduledAt ? (
                        <>
                          <CalendarClock className="size-3" />
                          {formatSendTime(row.scheduledAt, locale)}
                        </>
                      ) : (
                        fmtStamp(row.updatedAt)
                      )}
                    </span>
                  </span>
                  <span className="crm-item-sub block">
                    {row.attachmentCount ? <Paperclip className="mr-1 inline size-3 align-[-1px] text-muted-foreground" /> : null}
                    {row.subject || t("drafts.noSubject")}
                  </span>
                  {row.snippet ? <span className="crm-item-pre block">{row.snippet}</span> : null}
                  {row.status === "failed" && row.error ? (
                    <span className="mt-1 flex items-center gap-1 text-[11.5px] font-[550] text-destructive">
                      <AlertTriangle className="size-3 shrink-0" />
                      {draftErrorText(t, row.error)}
                    </span>
                  ) : row.status === "sending" ? (
                    <span className="crm-mut mt-1 block text-[11.5px]">{t("drafts.sendingNow")}</span>
                  ) : null}
                </span>
              </button>
            );
          })
        )}
      </div>

      <ContextMenu menu={menu} label={t("drafts.menu")}>
        {renderMenu}
      </ContextMenu>
    </div>
  );
});

/** The reading pane while a draft list is open: drafts open in the composer. */
export function DraftPaneEmpty({ kind }: { kind: DraftKind }) {
  const t = useT();
  return (
    <div className="crm-card crm-work-col">
      <div className="crm-pane-none">
        <FileText className="size-8 opacity-50" />
        <span className="max-w-[46ch] text-center">{t(`drafts.${kind}Pick`)}</span>
      </div>
    </div>
  );
}
